import { hashKey, type QueryClient, type QueryKey } from "@tanstack/react-query";
import {
  dependentKeys,
  entityKeys,
  resolveInvalidationKeys,
  type Facet,
  type InvalidationEntity,
} from "../entityDependencies";
import type { CacheEntry } from "./cacheSlots";
import type { BoundEntityCache } from "./entityCache";
import type { BulkResult } from "@/domain/types/bulk";

/**
 * What a mutation does to the cache, in three phases: `prepare` paints the
 * optimistic state and returns the snapshot to roll back to, `commit` writes
 * the server response, `settleKeys` lists what to reconcile in the background.
 */
export interface CacheEffect<D, V> {
  readonly scope?: string;
  /** Mutations of the same group reconcile once, after the last of them settles. */
  readonly group?: InvalidationEntity;
  prepare(client: QueryClient, variables: V): Promise<readonly CacheEntry[]>;
  commit(client: QueryClient, data: D, variables: V, snapshot: readonly CacheEntry[]): void;
  settleKeys(data: D | undefined, variables: V): readonly QueryKey[];
}

export interface PatchSpec<D, V, TId, TPatch> {
  id: (variables: V) => TId;
  optimistic?: (variables: V) => TPatch;
  fromResponse?: (data: D, variables: V) => TPatch;
  /** Serialises mutations of the same record so each one starts from the last. */
  scope?: string;
  /** Autosaves that write the full response and refresh on close skip background reconciliation. */
  reconcile?: boolean;
  /** Non-entity keys the change also makes stale (a pipeline heartbeat, a preview). */
  keys?: (data: D | undefined, variables: V) => readonly QueryKey[];
}

export interface RemoveSpec<V, TId> {
  id: (variables: V) => TId;
  optimistic?: boolean;
}

export type BulkSpec<D, V, TPatch> = {
  ids: (variables: V) => readonly string[];
  result: (data: D) => BulkResult;
} & ({ action: "patch"; optimistic?: (variables: V) => TPatch } | { action: "remove" });

export interface InvalidateSpec<D, V> {
  orgId: string;
  entities: readonly InvalidationEntity[];
  /** Own facets to reconcile; dependents are always included. Defaults to every facet. */
  facets?: readonly Facet[];
  keys?: (data: D | undefined, variables: V) => readonly QueryKey[];
}

export interface CacheBuilder<D, V> {
  patch<TId, TPatch, R>(
    entity: BoundEntityCache<TId, TPatch, R>,
    spec: PatchSpec<D, V, TId, TPatch>,
  ): CacheEffect<D, V>;
  create<TId, TPatch, R>(entity: BoundEntityCache<TId, TPatch, R>): CacheEffect<D, V>;
  remove<TId, TPatch, R>(
    entity: BoundEntityCache<TId, TPatch, R>,
    spec: RemoveSpec<V, TId>,
  ): CacheEffect<D, V>;
  bulk<TPatch, R>(
    entity: BoundEntityCache<string, TPatch, R>,
    spec: BulkSpec<D, V, TPatch>,
  ): CacheEffect<D, V>;
  invalidate(spec: InvalidateSpec<D, V>): CacheEffect<D, V>;
  /** Reads with no server-side effect (downloads, previews). */
  none(): CacheEffect<D, V>;
}

const NO_SNAPSHOT: readonly CacheEntry[] = [];

/**
 * Runs after the optimistic paint: a manual write becomes the state a cancelled
 * fetch reverts to, so the paint survives and is on screen in the same frame.
 */
async function cancel(client: QueryClient, slots: readonly { prefix: QueryKey }[]) {
  await Promise.all(slots.map((slot) => client.cancelQueries({ queryKey: slot.prefix })));
}

function writeIds<TId, TPatch, R>(
  client: QueryClient,
  entity: BoundEntityCache<TId, TPatch, R>,
  ids: readonly TId[],
  patch: TPatch | null,
): CacheEntry[] {
  const snapshot = new Map<string, CacheEntry>();
  for (const id of ids) {
    const change = entity.change(client, id, patch);
    for (const slot of entity.slots) {
      const entries = client.getQueriesData({ queryKey: slot.prefix });
      const changed =
        patch === null
          ? slot.removeAll(entries, id, change)
          : slot.patchAll(entries, id, patch, change);
      for (const [key, data] of changed) {
        const hash = hashKey(key);
        if (!snapshot.has(hash)) snapshot.set(hash, [key, client.getQueryData(key)]);
        client.setQueryData(key, data);
      }
    }
  }
  return [...snapshot.values()];
}

export function restore(client: QueryClient, snapshot: readonly CacheEntry[]) {
  for (const [key, data] of snapshot) client.setQueryData(key, data);
}

function dropRecords<TId, TPatch, R>(
  client: QueryClient,
  entity: BoundEntityCache<TId, TPatch, R>,
  ids: readonly TId[],
) {
  for (const id of ids) {
    for (const queryKey of entity.records(id)) client.removeQueries({ queryKey, type: "inactive" });
  }
}

function ownKeys<TId, TPatch, R>(
  entity: BoundEntityCache<TId, TPatch, R>,
  facets: readonly Facet[],
) {
  return [
    ...entityKeys(entity.entity, entity.orgId, facets),
    ...dependentKeys(entity.entity, entity.orgId),
  ];
}

const LIST_FACETS: readonly Facet[] = ["lists", "aggregates", "money"];

export function cacheBuilder<D, V>(): CacheBuilder<D, V> {
  return {
    patch: (entity, spec) => ({
      scope: spec.scope === undefined ? undefined : `${entity.entity}:${spec.scope}`,
      group: entity.entity,
      prepare: async (client, variables) => {
        if (!spec.optimistic) return NO_SNAPSHOT;
        const snapshot = writeIds(client, entity, [spec.id(variables)], spec.optimistic(variables));
        await cancel(client, entity.slots);
        return snapshot;
      },
      commit: (client, data, variables) => {
        if (spec.fromResponse)
          writeIds(client, entity, [spec.id(variables)], spec.fromResponse(data, variables));
      },
      settleKeys: (data, variables) =>
        spec.reconcile === false && data !== undefined
          ? []
          : [
              ...ownKeys(entity, LIST_FACETS),
              ...(spec.fromResponse && data !== undefined
                ? []
                : entity.records(spec.id(variables))),
              ...(spec.keys?.(data, variables) ?? []),
            ],
    }),

    create: (entity) => ({
      group: entity.entity,
      prepare: async () => NO_SNAPSHOT,
      commit: () => undefined,
      settleKeys: () => ownKeys(entity, LIST_FACETS),
    }),

    remove: (entity, spec) => ({
      group: entity.entity,
      prepare: async (client, variables) => {
        if (!spec.optimistic) return NO_SNAPSHOT;
        const snapshot = writeIds(client, entity, [spec.id(variables)], null);
        await cancel(client, entity.slots);
        return snapshot;
      },
      commit: (client, _data, variables) => {
        const id = spec.id(variables);
        if (!spec.optimistic) writeIds(client, entity, [id], null);
        dropRecords(client, entity, [id]);
      },
      settleKeys: () => ownKeys(entity, LIST_FACETS),
    }),

    bulk: (entity, spec) => ({
      group: entity.entity,
      prepare: async (client, variables) => {
        if (spec.action !== "patch" || !spec.optimistic) return NO_SNAPSHOT;
        const snapshot = writeIds(client, entity, spec.ids(variables), spec.optimistic(variables));
        await cancel(client, entity.slots);
        return snapshot;
      },
      commit: (client, data, variables, snapshot) => {
        const { succeeded, failed } = spec.result(data);
        if (spec.action === "remove") {
          writeIds(client, entity, succeeded, null);
          dropRecords(client, entity, succeeded);
        } else if (spec.optimistic && failed.length > 0) {
          restore(client, snapshot);
          writeIds(client, entity, succeeded, spec.optimistic(variables));
        }
      },
      settleKeys: () => ownKeys(entity, ["lists", "details", "aggregates"]),
    }),

    invalidate: (spec) => ({
      group: spec.entities.length === 1 ? spec.entities[0] : undefined,
      prepare: async () => NO_SNAPSHOT,
      commit: () => undefined,
      settleKeys: (data, variables) => [
        ...(spec.facets
          ? spec.entities.flatMap((entity) => [
              ...entityKeys(entity, spec.orgId, spec.facets),
              ...dependentKeys(entity, spec.orgId),
            ])
          : resolveInvalidationKeys(spec.entities, spec.orgId)),
        ...(spec.keys?.(data, variables) ?? []),
      ],
    }),

    none: () => ({
      prepare: async () => NO_SNAPSHOT,
      commit: () => undefined,
      settleKeys: () => [],
    }),
  };
}
